<script setup lang="ts">
import { computed } from 'vue';

import { useImage } from '@vueuse/core';

import importantIcon from '@/assets/icons/important.svg';
import type { Token } from '@/references/tokens';

import TokenImageCompound from '@/components/TokenImageCompound.vue';
import UiColoredCircle from '@/components/ui/UiColoredCircle.vue';
import UiIcon from '@/components/ui/UiIcon.vue';
import UiProgressCircle from '@/components/ui/UiProgressCircle.vue';
import UiSpinner from '@/components/ui/UiSpinner.vue';

interface Props {
  src?: string;
  fallbackSrc?: string;
  alt?: string;
  fallbackText?: string;
  fallbackColor?: string;
  loading?: boolean;
  failed?: boolean;
  loaderColor?: string;
  tokens?: [Token, Token];
  progress?: number;
}

const props = defineProps<Props>();

const { isLoading: imageLoading, error: imageError } = useImage({
  src: props.src ?? props.fallbackSrc ?? ''
});

const loaderBackgroundColor = computed(() => {
  if (props.failed) {
    return 'var(--color-red)';
  }

  return props.loaderColor ?? 'var(--color-blue)';
});
</script>

<template>
  <div class="transaction-icon">
    <TokenImageCompound v-if="props.tokens" :from="props.tokens[0]" :to="props.tokens[1]" />
    <UiColoredCircle
      v-else-if="imageLoading || imageError"
      :color="props.fallbackColor"
      :text="props.fallbackText"
    />
    <img
      v-else
      class="transaction-icon__image"
      :alt="props.alt"
      :src="props.src ?? props.fallbackSrc"
      width="44"
      height="44"
      role="img"
    />
    <UiProgressCircle
      v-if="props.progress"
      :progress="props.progress"
      :size="62"
      :stroke="8"
      class="transaction-icon__progress"
    />
    <div v-if="(props.loading || props.failed) && !progress" class="transaction-icon__status">
      <UiIcon v-if="props.failed" :name="importantIcon" :size="12" />
      <UiSpinner v-else-if="props.loading" :size="12" />
    </div>
  </div>
</template>

<style scoped>
.transaction-icon {
  position: relative;
  flex-shrink: 0;
}

.transaction-icon__image {
  display: block;
  width: 44px;
  height: 44px;
  border-radius: 50%;
}

.transaction-icon__status {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-white);
  background-color: v-bind(loaderBackgroundColor);
  border: 2px solid var(--color-background);
  border-radius: 50%;
}

.transaction-icon__progress {
  position: absolute;
  top: -9px;
  left: -9px;
}
</style>
